import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ConsoleDemandeService } from "@victor/api-client/console";
import { api } from "../../lib/api";
import { PageSpinner } from "../../ui/Spinner";
import {
  Panel,
  StatTile,
  Badge,
  fmtDate,
  fmtDateTime,
  initials,
} from "../tenants/tabs/_ui";

const CRENEAU_LABEL: Record<string, string> = {
  matin: "Matin",
  "apres-midi": "Après-midi",
  indifferent: "Indifférent",
};

export function DemandesServicesPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["demandes-services"],
    queryFn: () => api.demandesServices.list(),
  });
  const mut = useMutation({
    mutationFn: (v: { id: string; statut: ConsoleDemandeService["statut"] }) =>
      api.demandesServices.setStatut(v.id, v.statut),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["demandes-services"] }),
  });

  if (q.isLoading) return <PageSpinner />;
  const rows = q.data ?? [];
  const nouvelles = rows.filter((r) => r.statut === "nouvelle").length;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-navy-800 sm:text-3xl">
          Demandes de services
        </h1>
        <p className="mt-1 text-navy-600">
          Prestations Hubblot demandées par les agences depuis la Boutique
          (identité visuelle, site web, autres projets).
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile label="Total" value={rows.length} />
        <StatTile label="Nouvelles" value={nouvelles} />
        <StatTile label="Traitées" value={rows.length - nouvelles} />
      </div>

      {rows.length === 0 ? (
        <div className="flex min-h-[220px] items-center justify-center rounded-2xl bg-white ring-1 ring-navy-100">
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-navy-50 text-navy-300">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 13h6M9 17h4" /></svg>
            </div>
            <p className="text-sm text-navy-500">Aucune demande pour le moment.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((d) => {
            const nouvelle = d.statut === "nouvelle";
            return (
              <Panel key={d.id} accent={nouvelle ? "info" : undefined}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-xl text-xs font-bold ${
                        nouvelle
                          ? "bg-accent-100 text-accent-700"
                          : "bg-navy-100 text-navy-600"
                      }`}
                    >
                      {initials(d.agence)}
                    </span>
                    <div className="min-w-0">
                      <p className="font-bold text-navy-800">{d.agence}</p>
                      <p className="mt-0.5 truncate text-sm text-navy-500">
                        {d.contactNom} · {d.contactEmail}
                        {d.telephone ? ` · ${d.telephone}` : ""}
                      </p>
                    </div>
                  </div>
                  <Badge tone={nouvelle ? "accent" : "emerald"} dot>
                    {nouvelle ? "Nouvelle" : "Traitée"}
                  </Badge>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-50 px-3 py-1 text-sm font-semibold text-accent-700 ring-1 ring-accent-100">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7h-9M14 17H5M17 3l3 4-3 4M7 21l-3-4 3-4" /></svg>
                    {d.service}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-navy-50 px-3 py-1 text-sm text-navy-700 ring-1 ring-navy-100">
                    📅 {d.dateSouhaitee ? fmtDate(d.dateSouhaitee) : "à définir"}
                    {d.creneau ? ` · ${CRENEAU_LABEL[d.creneau] ?? d.creneau}` : ""}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-navy-50 px-3 py-1 text-sm text-navy-500 ring-1 ring-navy-100">
                    Reçu le {fmtDateTime(d.createdAt)}
                  </span>
                </div>

                {d.message ? (
                  <p className="mt-4 whitespace-pre-wrap rounded-xl bg-navy-50 p-3 text-sm text-navy-700 ring-1 ring-navy-100">
                    {d.message}
                  </p>
                ) : null}

                <div className="mt-4 flex justify-end gap-2">
                  <a
                    href={`mailto:${d.contactEmail}?subject=${encodeURIComponent(`Votre demande — ${d.service}`)}`}
                    className="inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold text-navy-600 ring-1 ring-navy-200 transition-colors hover:bg-navy-50"
                  >
                    Répondre par email
                  </a>
                  <button
                    type="button"
                    disabled={mut.isPending}
                    onClick={() =>
                      mut.mutate({
                        id: d.id,
                        statut: nouvelle ? "traitee" : "nouvelle",
                      })
                    }
                    className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
                      nouvelle
                        ? "bg-navy-800 text-white hover:bg-navy-700"
                        : "text-navy-600 ring-1 ring-navy-200 hover:bg-navy-50"
                    }`}
                  >
                    {nouvelle ? "Marquer comme traitée" : "Rouvrir"}
                  </button>
                </div>
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );
}
